import type { CategoryAttributeWriteItem } from '@/api/handlers/resources/categories/requests';
import type {
    AttributeType,
    CategoryAttributeItem,
    CategoryAttributeRole,
} from '@/api/models';

/**
 * Presentational config + pure helpers for the per-category attribute drawer
 * (Surface A). Logic-only (`.ts`); the sheet renders these. The drawer keeps a
 * **local draft** the user edits freely, then resolves it to one full-replace
 * PUT on save — so the draft carries each attribute's resolved `AttributeType`
 * (for rendering name/slug/input kind) alongside its category-scoped settings.
 */

/**
 * One editable row in the drawer. `attributeType` is the resolved dictionary
 * type (inlined by the read endpoint, or picked from the dictionary when
 * adding); the rest is the category-scoped assignment. `position` is *not*
 * stored — it's derived from the draft's order at save time.
 */
export type CategoryAttributeDraft = {
    attributeTypeId: string;
    role: CategoryAttributeRole;
    isRequired: boolean;
    attributeType: AttributeType;
};

/** The two roles, in display order, for the per-row role control. */
export const CATEGORY_ATTRIBUTE_ROLES: {
    id: CategoryAttributeRole;
    labelKey: string;
}[] = [
    {
        id: 'Variation',
        labelKey: 'categories.components.categoryAttributes.roles.variation',
    },
    {
        id: 'Descriptive',
        labelKey: 'categories.components.categoryAttributes.roles.descriptive',
    },
];

/** Seed the editable draft from the server's (position-ordered) link list. */
export const buildDraftFromItems = (
    items: CategoryAttributeItem[],
): CategoryAttributeDraft[] =>
    [...items]
        .sort((a, b) => a.position - b.position)
        .map((item) => ({
            attributeTypeId: item.attributeType.id,
            role: item.role,
            isRequired: item.isRequired,
            attributeType: item.attributeType,
        }));

/**
 * Resolve the draft to the full-replace PUT body. `position` is the 1-based
 * index in the current draft order, so reordering is captured implicitly.
 */
export const toWriteItems = (
    draft: CategoryAttributeDraft[],
): CategoryAttributeWriteItem[] =>
    draft.map((item, index) => ({
        attributeTypeId: item.attributeTypeId,
        role: item.role,
        isRequired: item.isRequired,
        position: index + 1,
    }));

/**
 * Dictionary types still available to add — every type not already in the
 * draft, sorted by name for a stable picker.
 */
export const getAvailableTypes = (
    allTypes: AttributeType[],
    draft: CategoryAttributeDraft[],
): AttributeType[] => {
    const used = new Set(draft.map((item) => item.attributeTypeId));
    return allTypes
        .filter((type) => !used.has(type.id))
        .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Whether the draft still matches the persisted set (same items, order, role
 * and required flags) — drives the Save button's enabled state so a no-op PUT
 * is never sent.
 */
export const isDraftDirty = (
    draft: CategoryAttributeDraft[],
    items: CategoryAttributeItem[],
): boolean => {
    const persisted = buildDraftFromItems(items);
    if (persisted.length !== draft.length) {
        return true;
    }
    return draft.some((item, index) => {
        const other = persisted[index];
        return (
            other === undefined ||
            other.attributeTypeId !== item.attributeTypeId ||
            other.role !== item.role ||
            other.isRequired !== item.isRequired
        );
    });
};
